import { useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { SelectOption, SelectOptionWithIcon } from "../../../interfaces/SelectOption";
import { useSettingsStore } from "../../../stores/SettingsStore";
import useAccessToken from "../../../hooks/AccessToken";
import { CustomField, SettingMultiMeetings } from "../../../interfaces/Settings";
import { getMeetingOptionMerchantTemplate, saveSetting } from "../../../api/SettingCRUD";
import { useIntl } from "react-intl";
import clsx from "clsx";
import * as Yup from 'yup';
import { convertFromMinutes, convertToMinutes, customNumberFormat } from "../../../functions/general";
import { useMeetingStore } from "../../../stores/MeetingsStore";
import useUser from "../../../hooks/User";
import { VirtualizedSelectOption } from "../../Select/SelectVirtualized";
import { ListDatabase } from "../ListDatabase";
import { GroupBase } from "react-select";

interface Props {
    optionType: GroupBase<SelectOptionWithIcon>[]
    optionsResetSerial: SelectOption[]
    dataPipeline: SelectOption[]
    dataMerchant: SelectOption[]
    dataMeeting: SelectOption[]
}

const StudioMeeting: FC<Props> = ({ optionType, optionsResetSerial, dataMerchant, dataPipeline, dataMeeting }) => {
    const intl = useIntl()
    const user = useUser()
    const token = useAccessToken()
    const { multiMeetings } = useMeetingStore();
    const { settings, setSettings } = useSettingsStore()

    const [widthProgress, setWidthProgress] = useState<number>(0)
    const [loadingMeetingPreferences, setLoadingMeetingPreferences] = useState(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const [fieldMeeting, setFieldMeeting] = useState<CustomField>({})
    const [countAutoCancel, setCountAutoCancel] = useState({
        day: 0,
        hour: 0,
        minute: 0
    })

    // -------- Meeting --------
    const [selectTemplate, setSelectTemplate] = useState<SettingMultiMeetings>({} as SettingMultiMeetings)
    const [resultData, setResultData] = useState<SettingMultiMeetings>({} as SettingMultiMeetings)

    const formik = useFormik({
        initialValues: {
            key: '',
            value: settings.meeting_title ? settings.meeting_title : intl.formatMessage({ id: "MENU.MEETINGS" }),
            checkAutoCancel: Number(settings.meeting_auto_cancel) > 0 ? true : false,
            valueAutoCancel: settings.meeting_auto_cancel ? settings.meeting_auto_cancel : -1,
            // valueVisitRadius: settings.radius_visit ? settings.radius_visit : 1,
            valueVisitRadius: 500,
            checkUpdateMeeting: settings.update_meeting,
        },
        validationSchema: Yup.object().shape({
            value: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })),
            valueVisitRadius: Yup.number().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.VISIT_RADIUS" }) })),
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            let isAutoCancelEmpty = false

            if (values.value === "") return false
            if (Number(values.valueVisitRadius) === 0) {
                Swal.fire({
                    title: intl.formatMessage({ id: "FORM.VALIDATION.RADIUS" }, { title: settings.meeting_title }),
                    icon: 'error',
                    confirmButtonText: 'Ok',
                    heightAuto: false
                })
                setLoadingMeetingPreferences(false)
                return false
            }
            if (values.valueVisitRadius < 0) return false
            if (values.checkAutoCancel && values.valueAutoCancel! <= 0) isAutoCancelEmpty = true
            if (isAutoCancelEmpty) {
                Swal.fire({
                    title: intl.formatMessage({ id: "FORM.VALIDATION.MEETING_CANCEL_TIME" }, { title: settings.meeting_title }),
                    icon: 'error',
                    confirmButtonText: 'Ok',
                    heightAuto: false
                })
                setLoadingMeetingPreferences(false)
                return false
            }

            setLoadingMeetingPreferences(true)

            try {
                const response = await saveSetting("meeting_title", values.value, token)
                const response2 = await saveSetting("meeting_auto_cancel", String(values.valueAutoCancel === 0 ? -1 : values.valueAutoCancel), token)
                const response3 = await saveSetting("radius_visit", String(values.valueVisitRadius), token)
                const response4 = await saveSetting("update_meeting", String(values.checkUpdateMeeting), token)
                if (response.data.success && response2.data.success && response3.data.success && response4.data.success) {
                    Swal.fire({
                        icon: 'success',
                        heightAuto: false,
                        title: intl.formatMessage({ id: "FORM.VALIDATION.SAVE_SUCCESS" }),
                        timer: 2000,
                        timerProgressBar: true
                    })
                    Object.values(fieldMeeting).forEach(field => {
                        field.new = false;
                    });
                    setSettings({
                        ...settings,
                        meeting_title: values.value,
                        meeting_auto_cancel: Number(values.valueAutoCancel),
                        radius_visit: Number(values.valueVisitRadius),
                        update_meeting: values.checkUpdateMeeting
                    })
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: error.respons.data.message,
                    confirmButtonText: 'Ok'
                })
            } finally {
                setLoadingMeetingPreferences(false)
            }
        }
    })

    const handleCheckAutoCancel = (checked: boolean) => {
        formik.setFieldValue('checkAutoCancel', checked)
        formik.setFieldValue('valueAutoCancel', -1)
        setCountAutoCancel({
            day: 0,
            hour: 0,
            minute: 0
        })
    }

    const handleCheckUpdateMeeting = (checked: boolean) => {
        formik.setFieldValue('checkUpdateMeeting', checked)
    }

    useEffect(() => {
        formik.setFieldValue("valueAutoCancel", (convertToMinutes(countAutoCancel)))
    }, [countAutoCancel])

    useEffect(() => {
        if (settings.meeting_custom_fields) {
            setFieldMeeting(JSON.parse(`${settings.meeting_custom_fields}`))
        }

        if (Number(settings.meeting_auto_cancel) > 0) {
            setCountAutoCancel(convertFromMinutes(Number(settings.meeting_auto_cancel)))
        }
    }, [settings])

    const getDataSelectedMeeting = async (item: SettingMultiMeetings) => {
        try {
            const [resMerchantTemplate] = await Promise.all([
                getMeetingOptionMerchantTemplate(token, item.id)
            ]);

            if (resMerchantTemplate.data.success) {
                const updateDataMeeting = {
                    ...item,
                    merchantTemplates: resMerchantTemplate.data.data.map((merchantTemplateItem: VirtualizedSelectOption) => ({
                        ...merchantTemplateItem,
                        label: merchantTemplateItem.name,
                        value: merchantTemplateItem.id,
                    }))
                }
                return updateDataMeeting
            }
        } catch (error: any) {
            console.error(`Error updating pipeline ${item.id}:`, error);
            Swal.fire({
                icon: 'error',
                title: error.response.data.message,
                confirmButtonText: 'Ok'
            })
        } finally {
        }
        return {
            ...item
        }
    };

    useEffect(() => {
        if (multiMeetings.length > 0) {
            let data = Object.keys(selectTemplate).length > 0 ? multiMeetings[multiMeetings.findIndex(item => item.id === selectTemplate.id)] : multiMeetings[0] ?? null
            setSelectTemplate(data)
            setWidthProgress(Math.round(multiMeetings.length / (user.data.companyFeatureLimitMeetingTemplate ?? 0) * 100))
        }
    }, [multiMeetings]);

    useEffect(() => {
        const fetchData = async () => {
            if (Object.keys(selectTemplate).length > 0) {
                setIsLoading(true)

                try {
                    const updatedTemplate = await getDataSelectedMeeting(selectTemplate);

                    setResultData(updatedTemplate);
                } catch (error) {
                    console.error('Error fetching pipeline template data:', error);
                } finally {
                    setIsLoading(false)
                }
            }
        };

        fetchData();
    }, [selectTemplate]);

    return (
        <>
            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0">
                    <div className="card-title m-0 w-100 d-flex justify-content-between">
                        <h3 className="fw-bolder m-0">{intl.formatMessage({ id: "FORM.LABEL.PREFERENCES" }, { title: settings.meeting_title ?? '' })}</h3>
                        <div className="d-flex align-items-center flex-column">
                            <div className="d-flex justify-content-between w-100 mt-auto mb-2 gap-5">
                                <span className="fw-bold fs-6 text-gray-400">{intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE_LIMIT' }, { title: settings.meeting_title })}</span>
                                <span className="fw-bolder fs-6">{customNumberFormat(multiMeetings.length ?? 0)} / {customNumberFormat(user.data.companyFeatureLimitMeetingTemplate ?? 0)}</span>
                            </div>
                            <div className="h-5px mx-3 w-100 mb-3" style={{ backgroundColor: "#f5f8fa" }}>
                                <div className="bg-success rounded h-5px" role="progressbar" style={{ width: (widthProgress >= 100 ? 100 : widthProgress) + "%" }}>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="kt_account_settings_email_preferences">
                    <form onSubmit={formik.handleSubmit} noValidate>
                        <div className="card-body border-top px-9 py-9">
                            <div className="row">
                                <span className="col-md-7 col-lg-9 col-xl-8 d-flex flex-column align-items-start">
                                    <span className="fw-bolder fs-5 mb-0">{`${intl.formatMessage({ id: "FORM.LABEL.NAME" })} `}</span>
                                    <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_NAME" })}</span>
                                </span>
                                <div className="col-md-5 col-lg-3 col-xl-4">
                                    <input
                                        {...formik.getFieldProps('value')}
                                        className={clsx(
                                            'form-control',
                                            { 'is-invalid': formik.touched.value && formik.errors.value },
                                        )}
                                        type='text'
                                        name='value'
                                    />
                                    {formik.touched.value && formik.errors.value && (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{formik.errors.value}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="separator separator-dashed my-6"></div>
                            <div className="row">
                                <label className="col-md-7 col-lg-9 col-xl-9 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer">
                                    <input className="form-check-input me-3" type="checkbox" {...formik.getFieldProps('checkUpdateMeeting')} checked={formik.values.checkUpdateMeeting!} onChange={(e) => handleCheckUpdateMeeting(e.target.checked)} />
                                    <span className="form-check-label d-flex flex-column align-items-start">
                                        <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.UPDATE_CUSTOM_FIELD" })} {settings.meeting_title}</span>
                                        <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_UPDATE_CUTOM_FIELD" }, { title: settings.meeting_title })}</span>
                                    </span>
                                </label>
                            </div>
                            <div className="separator separator-dashed my-6"></div>
                            <div className="row">
                                <label className="col-md-7 col-lg-9 col-xl-9 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer">
                                    <input className="form-check-input me-3" type="checkbox" {...formik.getFieldProps('checkAutoCancel')} checked={formik.values.checkAutoCancel!} onChange={(e) => handleCheckAutoCancel(e.target.checked)} />
                                    <span className="form-check-label d-flex flex-column align-items-start">
                                        <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.MEETING_AUTO_CANCEL_2" }, { title: settings.meeting_title })}</span>
                                        <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_MEETING_AUTO_CANCEL_2" }, { title: settings.meeting_title })}</span>
                                    </span>
                                </label>
                                {
                                    formik.values.checkAutoCancel &&
                                    <div className="col-md-5 col-lg-3 col-xl-3 d-flex justify-content-end align-items-center">
                                        <div className="d-flex align-items-center gap-2 me-3">
                                            <input
                                                value={countAutoCancel.day === 0 ? "" : countAutoCancel.day}
                                                className={'form-control ms-3'}
                                                type='text'
                                                name='value'
                                                style={{ border: "none", borderBottom: "2px solid #E4E6EF", width: "50px", textAlign: "right" }}
                                                onChange={(e) => { setCountAutoCancel({ ...countAutoCancel, day: Number((e.target.value).replace(/[^0-9]/g, '')) }) }}
                                            />
                                            <label>{intl.formatMessage({ id: "DAYS" })},</label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 me-3">
                                            <input
                                                value={countAutoCancel.hour === 0 ? "" : countAutoCancel.hour}
                                                className={'form-control ms-3'}
                                                type='text'
                                                name='value'
                                                style={{ border: "none", borderBottom: "2px solid #E4E6EF", width: "50px", textAlign: "right" }}
                                                onChange={(e) => { setCountAutoCancel({ ...countAutoCancel, hour: Number((e.target.value).replace(/[^0-9]/g, '')) }) }}
                                            />
                                            <label>{intl.formatMessage({ id: "HOURS" })},</label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                value={countAutoCancel.minute === 0 ? "" : countAutoCancel.minute}
                                                className={'form-control ms-3'}
                                                type='text'
                                                name='value'
                                                style={{ border: "none", borderBottom: "2px solid #E4E6EF", width: "50px", textAlign: "right" }}
                                                onChange={(e) => { setCountAutoCancel({ ...countAutoCancel, minute: Number((e.target.value).replace(/[^0-9]/g, '')) }) }}
                                            />
                                            <label>{intl.formatMessage({ id: "MINUTES" })}</label>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end py-6 px-9">
                            <button type="submit" className={`btn btn-primary ${loadingMeetingPreferences ? "disabled" : ""}`} data-kt-indicator={loadingMeetingPreferences ? 'on' : 'off'}>
                                <span className="indicator-label">
                                    {intl.formatMessage({ id: "FORM.ACTION.SAVE" })}
                                </span>
                                <span className="indicator-progress">
                                    Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <ListDatabase
                name={settings.meeting_title}
                type={"meeting"}
                listTemplate={multiMeetings}
                setSelectTemplate={setSelectTemplate}
                selectTemplate={selectTemplate}
                data={resultData}
                dataPipeline={dataPipeline}
                dataMeeting={dataMeeting}
                dataMerchant={dataMerchant}
                dataProgres={[]}
                dataProduct={[]}
                dataRole={[]}
                optionsResetSerial={optionsResetSerial}
                optionMerchantLink={[]}
                optionType={optionType}
                isLoading={isLoading}
            />
        </>
    )
}

export { StudioMeeting }